import { ApartmentRounded, BadgeRounded, ErrorOutlineRounded, MailOutline, MarkEmailUnreadOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Chip, CircularProgress, Container, CssBaseline, Divider, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { gateway } from "../../gateway";
import { IRoles } from "../../permissions/types/IRoles";
import { decodeToken } from "../../utils";
import { useAcceptInvitationMutation } from "./hooks/useAcceptAdminInvitation";
import { colors } from "../ui/colors";
import { useModals } from "../../context/ModalContext";

export interface IInvitationDecoded {
  uid: string;
  roleType: IRoles | string;
  isNewUser: boolean;
  companyId: number;
  companyName: string;
  email: string;
  iat: number;
  exp: number;
}

export enum InviteStatus {
  Idle = "idle",
  Loading = "loading",
  Valid = "valid",
  Invalid = "invalid",
  Accepted = "accepted",
}

interface AdminInvitationValidateProps {
  invitationUid?: string;
  onAccepted?: (invitation: IInvitationDecoded) => void | Promise<void>;
  onRejectClick?: () => void;
  onClose?: () => void;
  variant?: "page" | "modal";
  modalKey?: string;
  onRejected?: () => void;
}

const invitationTheme = {
  orange: colors.orange.base,
  orangeHover: colors.orange[700],
  orangeSoft: "rgba(242, 140, 40, 0.10)",
  orangeBorder: "rgba(242, 140, 40, 0.22)",
  textPrimary: "#1F2937",
  textSecondary: "#6B7280",
  textMuted: "#9CA3AF",
  border: "var(--fx-surface-border, #E2E8F0)",
  surface: "#FFFFFF",
  surfaceSoft: "#F9FAFB",
  pageBg: "#F6F7F9",
};

export const AdminInvitationValidate = ({
  invitationUid: invitationUidProp,
  onAccepted,
  onClose,
  variant = "page",
  modalKey,
  onRejected,
  onRejectClick,
}: AdminInvitationValidateProps) => {
  const { uid: invitationUidParam } = useParams();
  const [searchParams] = useSearchParams();
  const { closeModal } = useModals();

  const invitationUid = useMemo(() => {
    const fromProp = (invitationUidProp || "").trim();
    if (fromProp) return fromProp;

    const fromParam = (invitationUidParam || "").trim();
    if (fromParam) return fromParam;

    return (searchParams.get("invitationUid") || "").trim();
  }, [invitationUidProp, invitationUidParam, searchParams]);

  const acceptInvitationMutation = useAcceptInvitationMutation(invitationUid);

  const [inviteStatus, setInviteStatus] = useState<InviteStatus>(InviteStatus.Idle);
  const [inviteError, setInviteError] = useState<string>("");
  const [invite, setInvite] = useState<IInvitationDecoded | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!invitationUid) {
      setInviteStatus(InviteStatus.Invalid);
      setInviteError("Missing invitationUid.");
      return;
    }

    const run = async () => {
      setInviteStatus(InviteStatus.Loading);
      setInviteError("");

      try {
        const res = await gateway.get(`/invitations/validate/${encodeURIComponent(invitationUid)}`);
        const decoded = decodeToken(res?.data?.data) as IInvitationDecoded;

        if (!decoded) throw new Error("Could not decode invitation token.");

        if (!cancelled) {
          setInvite(decoded);
          setInviteStatus(InviteStatus.Valid);
        }
      } catch (e: any) {
        if (!cancelled) {
          const msg = e?.response?.data?.message;

          if (msg?.includes("already been used")) {
            setInviteStatus(InviteStatus.Invalid);
            setInviteError("This invitation has already been used or is no longer available.");
            return;
          }

          setInviteStatus(InviteStatus.Invalid);
          setInviteError(msg || "Could not validate invitation link.");
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [invitationUid]);

  const handleAccept = async () => {
    if (!invitationUid || !invite) return;

    try {
      setInviteError("");

      await acceptInvitationMutation.mutateAsync();

      await onAccepted?.(invite);
    } catch (e: any) {
      setInviteError(e?.response?.data?.message || e?.message || "Could not accept invitation.");

      setInviteStatus(InviteStatus.Invalid);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }

    if (modalKey) {
      closeModal(modalKey);
    }
  };

  const handleReject = () => {
    if (onRejectClick) {
      onRejectClick();
      return;
    }

    onRejected?.();
  };

  const InfoRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.75,
        p: 1.75,
        borderRadius: "14px",
        bgcolor: invitationTheme.surfaceSoft,
        border: `1px solid ${invitationTheme.border}`,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          minWidth: 40,
          borderRadius: "12px",
          display: "grid",
          placeItems: "center",
          bgcolor: invitationTheme.orangeSoft,
          border: `1px solid ${invitationTheme.orangeBorder}`,
          color: invitationTheme.orange,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: 11,
            lineHeight: 1.2,
            letterSpacing: "0.06em",
            color: invitationTheme.textMuted,
            textTransform: "uppercase",
            mb: 0.35,
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            fontWeight: 800,
            fontSize: 14.5,
            lineHeight: 1.35,
            color: invitationTheme.textPrimary,
            wordBreak: "break-word",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );

  const LoadingState = (
    <Box
      sx={{
        py: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 2,
      }}
    >
      <CircularProgress size={30} sx={{ color: invitationTheme.orange }} />

      <Box>
        <Typography sx={{ fontWeight: 900, color: invitationTheme.textPrimary }}>Validating invitation</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: invitationTheme.textSecondary }}>Please wait while we verify this link.</Typography>
      </Box>
    </Box>
  );

  const InvalidState = (
    <Alert
      severity="error"
      icon={<ErrorOutlineRounded />}
      sx={{
        mt: 1,
        borderRadius: "14px",
        fontWeight: 700,
        alignItems: "center",
      }}
    >
      {inviteError}
    </Alert>
  );

  const Card = (
    <Box
      sx={{
        width: "100%",
        maxWidth: variant === "modal" ? 560 : "100%",
        mx: "auto",
        borderRadius: "22px",
        border: `1px solid ${invitationTheme.border}`,
        background: invitationTheme.surface,
        overflow: "hidden",
        boxShadow: variant === "modal" ? "0px 22px 60px rgba(15, 23, 42, 0.16)" : "0px 18px 50px rgba(15, 23, 42, 0.10)",
      }}
    >
      <Box
        sx={{
          height: 5,
          background: `linear-gradient(90deg, ${invitationTheme.orange}, rgba(242, 140, 40, 0.35))`,
        }}
      />

      <Box sx={{ p: { xs: 2.5, sm: 3.25 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Stack direction="row" spacing={1.75} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                minWidth: 48,
                borderRadius: "16px",
                display: "grid",
                placeItems: "center",
                bgcolor: invitationTheme.orangeSoft,
                color: invitationTheme.orange,
                border: `1px solid ${invitationTheme.orangeBorder}`,
              }}
            >
              <ApartmentRounded sx={{ fontSize: 25 }} />
            </Box>

            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: { xs: 20, sm: 22 },
                    lineHeight: 1.15,
                    fontWeight: 950,
                    color: invitationTheme.textPrimary,
                    letterSpacing: "-0.03em",
                  }}
                >
                  {invite?.companyName ? `Join ${invite.companyName}` : "Company Invitation"}
                </Typography>

                {inviteStatus === InviteStatus.Valid && (
                  <Chip
                    label="Pending"
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: 11,
                      fontWeight: 900,
                      color: invitationTheme.orange,
                      bgcolor: invitationTheme.orangeSoft,
                      border: `1px solid ${invitationTheme.orangeBorder}`,
                    }}
                  />
                )}
              </Stack>

              <Typography
                sx={{
                  maxWidth: 390,
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  fontWeight: 600,
                  color: invitationTheme.textSecondary,
                }}
              >
                Review the invitation details below before joining this company.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ my: 2.5, borderColor: invitationTheme.border }} />

        {inviteStatus === InviteStatus.Loading && LoadingState}

        {inviteStatus !== InviteStatus.Loading && invite && (
          <>
            <Stack spacing={1.25}>
              <InfoRow icon={<ApartmentRounded sx={{ fontSize: 20 }} />} label="Company" value={invite.companyName} />

              <InfoRow icon={<MailOutline sx={{ fontSize: 20 }} />} label="Invited email" value={invite.email} />

              <InfoRow icon={<BadgeRounded sx={{ fontSize: 20 }} />} label="Role" value={String(invite.roleType)} />
            </Stack>

            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: "14px",
                bgcolor: "rgba(15, 23, 42, 0.035)",
                border: `1px dashed ${invitationTheme.border}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12.5,
                  lineHeight: 1.45,
                  fontWeight: 600,
                  color: invitationTheme.textSecondary,
                }}
              >
                This invitation was sent to{" "}
                <Box component="span" sx={{ fontWeight: 900, color: invitationTheme.textPrimary }}>
                  {invite.email}
                </Box>
                . Make sure you are using the correct account before accepting.
              </Typography>
            </Box>

            {inviteStatus === InviteStatus.Invalid && InvalidState}

            <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.25} sx={{ mt: 3 }}>
              {variant === "modal" && (
                <Button
                  fullWidth
                  onClick={handleClose}
                  disabled={acceptInvitationMutation.isPending}
                  sx={{
                    height: 48,
                    borderRadius: "13px",
                    fontWeight: 900,
                    color: invitationTheme.textPrimary,
                    bgcolor: invitationTheme.surfaceSoft,
                    border: `1px solid ${invitationTheme.border}`,
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: "var(--fx-surface-muted, #F1F5F9)",
                    },
                  }}
                >
                  Maybe later
                </Button>
              )}

              <Button
                variant="contained"
                fullWidth
                onClick={handleAccept}
                disabled={acceptInvitationMutation.isPending}
                startIcon={acceptInvitationMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <MarkEmailUnreadOutlined />}
                sx={{
                  height: 48,
                  borderRadius: "13px",
                  fontWeight: 950,
                  bgcolor: invitationTheme.orange,
                  color: "#FFFFFF",
                  textTransform: "none",
                  boxShadow: "0px 10px 22px rgba(242, 140, 40, 0.24)",
                  "&:hover": {
                    bgcolor: invitationTheme.orangeHover,
                    boxShadow: "0px 12px 26px rgba(242, 140, 40, 0.30)",
                  },
                  "&:disabled": {
                    bgcolor: "rgba(242, 140, 40, 0.55)",
                    color: "#FFFFFF",
                  },
                }}
              >
                {acceptInvitationMutation.isPending ? "Accepting invitation..." : "Accept & Join"}
              </Button>

              {variant === "modal" && (
                <Button
                  fullWidth
                  onClick={handleReject}
                  disabled={acceptInvitationMutation.isPending}
                  sx={{
                    height: 48,
                    borderRadius: "13px",
                    fontWeight: 900,
                    color: "#B91C1C",
                    bgcolor: "rgba(185, 28, 28, 0.06)",
                    border: "1px solid rgba(185, 28, 28, 0.18)",
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: "rgba(185, 28, 28, 0.10)",
                    },
                  }}
                >
                  Reject
                </Button>
              )}
            </Stack>
          </>
        )}

        {inviteStatus === InviteStatus.Invalid && !invite && InvalidState}
      </Box>
    </Box>
  );

  if (variant === "modal") return <>{Card}</>;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: invitationTheme.pageBg,
        display: "grid",
        placeItems: "center",
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <CssBaseline />
        {Card}
      </Container>
    </Box>
  );
};
